import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { ConfigurationService } from '../../configuration.service';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { Question } from '../../firestore-models/question';
import { ApiQuestion } from '../../api-client-module';

import { AppConst } from '../../app-const';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-select-questions-dialog',
  templateUrl: './select-questions-dialog.component.html',
  styleUrls: ['./select-questions-dialog.component.css'],
  standalone: false,
})
export class SelectQuestionsDialogComponent implements OnInit {

  projectLanguages!: Array<ProjectLanguage>;

  private questionsCollection!: AngularFirestoreCollection<Question>;
  questionsObservable!: Observable<Question[]>;
  questionsSubscribe!: Subscription;
  projectId: string;

  displayedColumns: string[] = ['position', 'select', 'question_id', 'question_name', 'question_type', 'questions'];
  dataSource = new MatTableDataSource<Question>();
  selection = new SelectionModel<Question>(true, []);

  hallsCollectionName = AppConst.FIRESTORE_HALLS;

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialogRef: MatDialogRef<SelectQuestionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ApiQuestion,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
      this.projectId = this.configurationService.getProjectId();
    }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.questionsCollection = this.angularFirestore.collection<Question>(
      AppConst.FIRESTORE_QUESTIONS, ref => ref.where('project_id', '==', this.projectId).orderBy('display_no', 'asc'));
    this.questionsObservable = this.questionsCollection.valueChanges({ idField: 'id' });
    this.questionsSubscribe = this.questionsObservable.subscribe(questions => {
      this.dataSource.data = this.addFilterValue(questions);
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.questionsSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Question): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  selectQuestions() {
    let questions: Array<ApiQuestion> = new Array<ApiQuestion>();
    for (const row of this.dataSource.data) {
      if (this.selection.isSelected(row)) {
        const question: ApiQuestion = {
          id: row.id,
          question_id: row.question_id,
          display_no: row.display_no,
          question_names: row.question_names,
          question_comps: row.question_comps,
          question_type: row.question_type,
          question_select_items: row.question_select_items,
          question_check: row.question_check,
          question_check_range_upper: row.question_check_range_upper,
          question_check_range_lower: row.question_check_range_lower,
          question_check_date_range_upper: row.question_check_date_range_upper?.toDate(),
          question_check_date_range_lower: row.question_check_date_range_lower?.toDate(),
          question_check_base_date: row.question_check_base_date?.toDate(),
          question_check_base_date_to_start: row.question_check_base_date_to_start,
          question_check_base_date_to_end: row.question_check_base_date_to_end,
          question_check_col_count_upper: row.question_check_col_count_upper,
          question_check_col_count_lower: row.question_check_col_count_lower,
          project_id: row.project_id,
          enabled: row.enabled,
        };
        questions.push(question);
      }
    }
    this.dialogRef.close(questions);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'question_id':
          return compare(a.question_id, b.question_id, isAsc);
        case 'question_name':
          const aName = a.question_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          const bName = b.question_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          return compare(aName, bName, isAsc);
        case 'question_type':
          return compare(a.question_type, b.question_type, isAsc);
        default:
          return 0;
      }
    });
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      // for (const value of doc.hall_names) {
      //   filterValue += value.name + ' '
      // }
      for (const value of doc.question_names) {
        filterValue += value.name + ' '
      }
      // filterValue += doc.floor_explain + ' ';
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
